import { notifications } from '@mantine/notifications';
import type { NavigateFunction } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { getProductForEdit } from '@/Api';
import { clientRoutes } from '@/ApiPaths';
import type { IProduct } from '@/types/Product.model';
import {
  isNotFoundError,
  isPermissionError,
  isProductInTrashError,
} from '@/utils/errorHandling';

export type LoadProductForEditOutcome =
  | { status: 'ok'; product: IProduct }
  | { status: 'forbidden' }
  | { status: 'inTrash' }
  | { status: 'notFound' }
  | { status: 'error'; error: unknown };

/** Loads a product for the edit UI; redirects non-owners to the dashboard. */
export async function loadProductForEdit(
  productId: string,
  navigate: NavigateFunction,
  t: TFunction
): Promise<LoadProductForEditOutcome> {
  try {
    const product = await getProductForEdit(productId);
    return { status: 'ok', product };
  } catch (error) {
    if (isPermissionError(error)) {
      notifications.show({
        title: t('editProduct.notOwnerTitle'),
        message: t('editProduct.notOwnerMessage'),
        color: 'red',
      });
      navigate(clientRoutes.dashboard);
      return { status: 'forbidden' };
    }
    if (isProductInTrashError(error)) {
      return { status: 'inTrash' };
    }
    if (isNotFoundError(error)) {
      return { status: 'notFound' };
    }
    return { status: 'error', error };
  }
}
